import { Location } from '@angular/common';
import { Component, OnInit } from '@angular/core';
import { Router } from '@angular/router';

import { ErrorPageState, DEFAULT_ERROR_PAGE_STATE } from './error-page-state.interface';

/**
 * Generic error page component that displays an error message with a back button.
 * 
 * Receives configuration via router state (Angular best practice).
 * Falls back to default values if no state is provided.
 * 
 * Usage:
 * ```typescript
 * router.navigate(['/error'], {
 *   state: {
 *     title: 'Failed to load opportunity',
 *     message: 'The opportunity could not be loaded.',
 *     backRoute: ['/Opportunities'],
 *     backLabel: 'Back to Opportunities'
 *   }
 * });
 * ```
 */
@Component({
  selector: 'spark-core-error-page',
  templateUrl: './error-page.component.html',
  styleUrls: ['./error-page.component.scss']
})
export class ErrorPageComponent implements OnInit {
  title: string = DEFAULT_ERROR_PAGE_STATE.title!;
  message: string = DEFAULT_ERROR_PAGE_STATE.message!;
  backRoute?: string[];
  backLabel: string = DEFAULT_ERROR_PAGE_STATE.backLabel!;

  constructor(
    private router: Router,
    private location: Location
  ) {}

  ngOnInit(): void {
    // Read error state from router navigation (passed via router.navigate state)
    const state = history.state as ErrorPageState | undefined;

    if (state) {
      this.title = state.title ?? DEFAULT_ERROR_PAGE_STATE.title!;
      this.message = state.message ?? DEFAULT_ERROR_PAGE_STATE.message!;
      this.backRoute = state.backRoute;
      this.backLabel = state.backLabel ?? DEFAULT_ERROR_PAGE_STATE.backLabel!;
    }
  }

  onBackClick(): void {
    if (this.backRoute) {
      // Navigate to explicit route if provided
      this.router.navigate(this.backRoute);
    } else {
      // Use browser back navigation to return to previous page
      this.location.back();
    }
  }
}

